import api from "./api"
import { AxiosError } from "axios"
import { ManagementCompany } from "../Interfaces";
import { showErroPage } from "../utils/helper";
import { normalizeCompany, normalizeCompanyList } from "../utils/companyAccess";

export type CompanyCreateField = "name" | "code" | "form"

export class CompanyCreateError extends Error {
  status: number
  field: CompanyCreateField

  constructor(status: number, field: CompanyCreateField, message: string) {
    super(message)
    this.name = "CompanyCreateError"
    this.status = status
    this.field = field
  }
}

function axiosStatus(error: unknown): number | undefined {
  if (error && typeof error === "object" && "response" in error) {
    return (error as { response?: { status?: number } }).response?.status
  }
  return undefined
}

function axiosMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const data = (error as { response?: { data?: { message?: string, error?: string } } }).response?.data
    if (data?.message) return String(data.message)
    if (data?.error) return String(data.error)
  }
  if (error instanceof Error) return error.message
  return ""
}

class ManagementCompanyService {
  private baseUrl = "/managementCompany";

  /**
   * ConstructionWalk add-company: POST { name, code } on the existing
   * management-company resource. Companion API should return 201 + list shape,
   * 409 for a duplicate code, 400 for empty fields, 403 for forbidden.
   */
  public createFromNameAndCode = async (input: { name: string, code: string }): Promise<ManagementCompany> => {
    const name = input.name.trim()
    const code = input.code.trim()
    try {
      const response = await api.post(this.baseUrl, { name, code })
      return normalizeCompany(response.data)
    } catch (error) {
      const status = axiosStatus(error) ?? (error instanceof AxiosError ? error.response?.status : undefined) ?? 500
      const message = axiosMessage(error).toLowerCase()
      if (status === 409) {
        throw new CompanyCreateError(409, "code", "duplicate")
      }
      if (status === 400) {
        if (message.includes("name")) throw new CompanyCreateError(400, "name", "empty-name")
        if (message.includes("code")) throw new CompanyCreateError(400, "code", "empty-code")
        throw new CompanyCreateError(400, "form", "failed")
      }
      throw new CompanyCreateError(status, "form", "failed")
    }
  }

  public listForUser = async (userId: number): Promise<ManagementCompany[]> => {
    const raw = await this.getAll(userId)
    if (Array.isArray(raw)) return normalizeCompanyList(raw)
    return []
  }

  public resolveForUser = async (userId: number, preferredId?: number): Promise<ManagementCompany | undefined> => {
    try {
      if (preferredId) {
        const one = await this.getOne(preferredId)
        if (one) return normalizeCompany(one)
      }
    } catch {
      // Fall through to the user list.
    }
    try {
      const companies = await this.listForUser(userId)
      if (preferredId) {
        const match = companies.find((company) => company.id === preferredId)
        if (match) return match
      }
      return companies[0]
    } catch {
      return undefined
    }
  }

  public create = async (managementCompanyInfo: any, imageFile: File | null = null) => {
    if (!managementCompanyInfo) throw new Error("No management company info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}`,
        managementCompanyInfo,
      );
      if (imageFile) await this.uploadLogo(imageFile, response.data.id, response.data.name)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public uploadLogo = async (imageFile: File, managementCompanyId: number, managementCompanyName: string) => {
    if (!imageFile) return;
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/update/logo/${managementCompanyId}/${managementCompanyName}`,
      )
      if (imageFile.type !== "image/jpeg" && imageFile.type !== "image/png" && imageFile.type !== "image/jpg") throw new Error("Invalid file type")
      const response = await api.put(
        signedUrl,
        imageFile,
        {
          headers: {
            "Content-Type": imageFile.type,
            "Authorization": undefined,
          }
        }
      );
      return response.data;
    } catch (error) {
      return error as Error;
    }
  };

  public getViewableLogo = async (managementCompanyId: number, managementCompanyName: string) => {
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/logo/${managementCompanyId}/${managementCompanyName}`,
      )
      return signedUrl;
    } catch (error) {
      return error as Error;
    }
  }

  public getOne = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public getByLocation = async (locationId: number): Promise<ManagementCompany | undefined> => {
    if (!locationId) throw new Error("No locationId provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/by-location/${locationId}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        const statusText = error.response?.statusText
        throw new Error(statusText)
      } else {
        console.error(error)
        showErroPage()
        return undefined
      }
    }
  }

  public getByUser = async (userId: number): Promise<ManagementCompany | undefined> => {
    if (!userId) throw new Error("No userId provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/by-user/${userId}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        const statusText = error.response?.statusText
        throw new Error(statusText)
      } else {
        console.error(error)
        showErroPage()
        return undefined
      }
    }
  }

  public getAll = async (userId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/by-user/${userId}`
      );
      return response.data.companies
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public getSome = async (userId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const offset = (page - 1) * limit;
      const response = await api.get(
        `${this.baseUrl}/some?limit=${limit}&offset=${offset}&searchTerm=${searchTerm || ""}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public update = async (managementCompanyInfo: any, imageFile: File | null = null) => {
    if (!managementCompanyInfo) throw new Error("No management company info provided")
    try {
      const response = await api.put(
        `${this.baseUrl}`,
        managementCompanyInfo
      );
      if (imageFile) await this.uploadLogo(imageFile, response.data.id, response.data.name)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public delete = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.delete(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }
}

export const managementCompanyService = new ManagementCompanyService()

